import L from "leaflet";
import { useEffect, useRef } from "react";
import { useMap } from "react-leaflet";
import { CapaMapa, ListadoItem } from "../../interfaces/Indicator";
import { API_FILES_BASE_URL } from "../../utils/const";

const parseCoordenadas = (coordenadas: string): [number, number] | null => {
    const parts = coordenadas.split(",").map((v) => parseFloat(v.trim()));
    if (parts.length !== 2 || parts.some((n) => Number.isNaN(n))) return null;
    const [lng, lat] = parts;
    return [lat, lng]; //[lat, lng]
};

const buildCapaIcon = (imagen: string) =>
    L.icon({
        iconUrl: `${API_FILES_BASE_URL}${imagen.replace("files/", "")}`,
        iconSize: [28, 28],
        iconAnchor: [14, 28],
        popupAnchor: [0, -28],
    });

interface MapLayersProps {
    capasMapa?: CapaMapa[];
    activeCapaIds?: number[];
    getColor?: (capa: CapaMapa) => string;
    renderTooltip?: (item: ListadoItem, capa: CapaMapa) => string; 
}

const buildCapaLayerGroup = (
    capa: CapaMapa,
    icon: L.Icon,
    renderTooltip?: (item: ListadoItem, capa: CapaMapa) => string,
): L.LayerGroup => {
    const layerGroup = L.layerGroup();

    capa.items?.forEach((item) => {
        if (!item.estado) return;
        const coords = parseCoordenadas(item.coordenadas);
        if (!coords) return;

        const marker = L.marker(coords, {
            icon,
            pane: 'markerPane',
            bubblingMouseEvents: true,
        });

        const popupContent = renderTooltip
            ? renderTooltip(item, capa)
            : `<strong>${capa.nombre}</strong><br/>${item.nombre}`;

        marker.bindPopup(popupContent, {
            closeButton: true,
            autoPan: true,
            maxWidth: 260,
        });

        marker.addTo(layerGroup);
    });

    return layerGroup;
};

const MapLayers = ({
    capasMapa,
    activeCapaIds,
    getColor,
    renderTooltip,
}: MapLayersProps) => {
    const map = useMap();
    const iconCache = useRef<Map<number, L.Icon>>(new Map());

    const rootGroupRef = useRef<L.LayerGroup | null>(null);

    const capaGroupsRef = useRef<Map<number, { group: L.LayerGroup; source: CapaMapa }>>(new Map());

    useEffect(() => {
        if (!map) return;

        const rootGroup = L.layerGroup();
        rootGroup.addTo(map);
        rootGroupRef.current = rootGroup;

        return () => {
            rootGroup.clearLayers();
            map.removeLayer(rootGroup);
            rootGroupRef.current = null;
            capaGroupsRef.current.clear();
        };
    }, [map]);

  
    useEffect(() => {
        const rootGroup = rootGroupRef.current;
        if (!rootGroup || !capasMapa?.length) return;

        const capaGroups = capaGroupsRef.current;
        const currentCapaIds = new Set(capasMapa.map((c) => c.id));

        capaGroups.forEach((entry, capaId) => {
            if (!currentCapaIds.has(capaId)) {
                rootGroup.removeLayer(entry.group);
                entry.group.clearLayers();
                capaGroups.delete(capaId);
                iconCache.current.delete(capaId);
            }
        });

        capasMapa.forEach((capa) => {
            if (!capa.estado) return;

            const existing = capaGroups.get(capa.id);

            if (existing && existing.source === capa) return;

            if (existing) {
                rootGroup.removeLayer(existing.group);
                existing.group.clearLayers();
            }

            if (!iconCache.current.has(capa.id)) {
                iconCache.current.set(capa.id, buildCapaIcon(capa.imagen));
            }
            const icon = iconCache.current.get(capa.id)!;

            const newGroup = buildCapaLayerGroup(capa, icon, renderTooltip);
            capaGroups.set(capa.id, { group: newGroup, source: capa });
        });
    }, [capasMapa, renderTooltip]);

    useEffect(() => {
        const rootGroup = rootGroupRef.current;
        if (!rootGroup || !capasMapa?.length) return;

        const activeSet = new Set(activeCapaIds ?? capasMapa.map((c) => c.id));
        const capaGroups = capaGroupsRef.current;

        capaGroups.forEach((entry, capaId) => {
            const shouldBeVisible = activeSet.has(capaId);
            const isVisible = rootGroup.hasLayer(entry.group);

            if (shouldBeVisible && !isVisible) {
                rootGroup.addLayer(entry.group);
            } else if (!shouldBeVisible && isVisible) {
                rootGroup.removeLayer(entry.group);
            }
        });
    }, [capasMapa, activeCapaIds]);

    return null;
};

export default MapLayers;